import { MessageContext } from "contexts/MessageContext";
import React, { memo, useContext } from "react";

import {
  type IAction,
  type IMessageElement,
  type IStep,
} from "@chainlit/react-client";

import BlinkingCursor from "@/components/BlinkingCursor";
import HI from "@/components/webdock/hi";

import { Message } from "./Message";
import { useTheme } from "@/components/ThemeProvider";

interface Props {
  messages: IStep[];
  elements: IMessageElement[];
  actions: IAction[];
  indent: number;
  isRunning?: boolean;
  scorableRun?: IStep;
}

const CL_RUN_NAMES = ["on_chat_start", "on_message", "on_audio_end"];

const hasActiveToolStep = (step: IStep): boolean => {
  return (
    step.steps?.some(
      (s) =>
        (s.type === "tool" && s.start && !s.end && !s.isError) ||
        s.type.includes("message") ||
        hasActiveToolStep(s),
    ) || false
  );
};

const hasAssistantMessage = (step: IStep): boolean => {
  return (
    step.steps?.some(
      (s) => s.type === "assistant_message" || hasAssistantMessage(s),
    ) || false
  );
};

const Messages = memo(
  ({ messages, elements, actions, indent, isRunning, scorableRun }: Props) => {
    const messageContext = useContext(MessageContext);
    return (
      <>
        {messages.map((m, idx) => {
          // Handle chainlit runs
          if (CL_RUN_NAMES.includes(m.name)) {
            const isRunning = !m.end && !m.isError && messageContext.loading;
            const isToolCallCoT = messageContext.cot === "tool_call" ||
              messageContext.cot === "full";
            const isHiddenCoT = messageContext.cot === "hidden";

            const showToolCoTLoader = isToolCallCoT
              ? isRunning && !hasActiveToolStep(m)
              : false;

            const showHiddenCoTLoader = isHiddenCoT
              ? isRunning && !hasAssistantMessage(m)
              : false;
            // Ignore on_chat_start for scorable run
            const scorableRun = !isRunning && m.name !== "on_chat_start"
              ? m
              : undefined;
            return (
              <React.Fragment key={m.id}>
                {m.steps?.length
                  ? (
                    <Messages
                      messages={m.steps}
                      elements={elements}
                      actions={actions}
                      indent={indent}
                      isRunning={isRunning}
                      scorableRun={scorableRun}
                    />
                  )
                  : null}
                {(showToolCoTLoader || showHiddenCoTLoader) &&
                  m.name !== "on_chat_start"
                  ? <BlinkingCursor />
                  : null}
                <MessageSeperator
                  step={m}
                />
              </React.Fragment>
            );
          } else {
            // Score the current run
            const _scorableRun = m.type === "run" ? m : scorableRun;
            // The message is scorable if it is the last assistant message of the run

            const isRunLastAssistantMessage = m ===
              _scorableRun?.steps?.findLast(
                (_m) => _m.type === "assistant_message",
              );

            const isLastAssistantMessage = messages.findLast((_m) =>
              _m.type === "assistant_message"
            ) === m;

            const isScorable = isRunLastAssistantMessage ||
              isLastAssistantMessage;

            return (
              <>
                <Message
                  message={m}
                  elements={elements}
                  actions={actions}
                  key={m.id}
                  indent={indent}
                  isRunning={isRunning}
                  scorableRun={_scorableRun}
                  isScorable={isScorable}
                />
                <MessageSeperator
                  step={m}
                />
              </>
            );
          }
        })}
      </>
    );
  },
);

export { Messages };

function MessageSeperator(
  { step }: { step: IStep },
) {
  const { variant } = useTheme();
  const color = variant === "dark" ? "bg-[#B5B5B526]" : "bg-[#00000026]";
  if (step.type == "assistant_message") {
    return <hr style={{
      "margin": "30px 0"
    }} className={`border-none h-px ${color}`} />;
  }
  return null;
}
